import {
  type TwoFactorEnableResponse,
} from "@nexys/user-management-client";
import { AlertCircle, Check, Copy, ShieldCheck, ShieldOff } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";

type Step = "idle" | "scan" | "codes";

/** Enable / disable TOTP two-factor authentication. */
export const TwoFactorCard = () => {
  const client = useUmClient();
  const session = useCall("getSession");
  const enabled = session.data?.user.twoFactorEnabled ?? false;

  const [step, setStep] = useState<Step>("idle");
  const [enrolment, setEnrolment] = useState<TwoFactorEnableResponse | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const begin = async () => {
    setBusy(true);
    setError(null);
    const res = await client.twoFactorEnable();
    setBusy(false);
    if (res.ok) {
      setEnrolment(res.data);
      setStep("scan");
    } else {
      setError(errorMessage(res.error, "Couldn't start 2FA setup."));
    }
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await client.twoFactorConfirm({ code: code.trim() });
    setBusy(false);
    if (res.ok) {
      setCode("");
      setStep("codes");
      await session.refresh();
    } else {
      setError(errorMessage(res.error, "That code didn't match — try again."));
    }
  };

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await client.twoFactorDisable({ code: code.trim() });
    setBusy(false);
    if (res.ok) {
      setCode("");
      setStep("idle");
      setEnrolment(null);
      await session.refresh();
    } else {
      setError(errorMessage(res.error, "That code didn't match — try again."));
    }
  };

  const copyCodes = async () => {
    if (!enrolment) return;
    await navigator.clipboard.writeText(enrolment.backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted-foreground" />
          Two-factor authentication
          {enabled ? <Badge variant="secondary">Enabled</Badge> : null}
        </CardTitle>
        <CardDescription>
          Require a one-time code from an authenticator app when signing in.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {enabled && step === "idle" ? (
          <form onSubmit={disable} className="grid gap-3">
            <Field
              label="Enter a current code to turn off 2FA"
              htmlFor="totp-disable"
            >
              <Input
                id="totp-disable"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="max-w-40 font-mono"
              />
            </Field>
            <div>
              <Button
                type="submit"
                variant="destructive"
                loading={busy}
                disabled={code.trim().length === 0}
              >
                <ShieldOff />
                Disable 2FA
              </Button>
            </div>
          </form>
        ) : null}

        {!enabled && step === "idle" ? (
          <div>
            <Button onClick={begin} loading={busy}>
              <ShieldCheck />
              Enable 2FA
            </Button>
          </div>
        ) : null}

        {step === "scan" && enrolment ? (
          <div className="grid gap-4">
            <p className="text-sm text-muted-foreground">
              Scan this QR code with your authenticator app, then enter the
              6-digit code it shows.
            </p>
            <div className="flex items-start gap-6">
              <div className="rounded-lg border bg-white p-3">
                <QRCodeSVG value={enrolment.uri} size={144} />
              </div>
              <div className="grid gap-1 text-sm">
                <span className="text-muted-foreground text-xs">
                  Or enter the secret manually:
                </span>
                <code
                  data-testid="totp-secret"
                  className="break-all rounded bg-muted px-2 py-1 font-mono text-xs"
                >
                  {enrolment.secret}
                </code>
              </div>
            </div>
            <form onSubmit={confirm} className="flex items-end gap-3">
              <Field label="Verification code" htmlFor="totp-confirm">
                <Input
                  id="totp-confirm"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="max-w-40 font-mono"
                />
              </Field>
              <Button type="submit" loading={busy} disabled={code.trim().length === 0}>
                Verify & enable
              </Button>
            </form>
          </div>
        ) : null}

        {step === "codes" && enrolment ? (
          <div className="grid gap-3">
            <Alert variant="success">
              <ShieldCheck />
              <AlertDescription>
                Two-factor authentication is on. Save these backup codes now —
                they are shown only once and each works a single time.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-4 sm:grid-cols-5">
              {enrolment.backupCodes.map((c) => (
                <code key={c} className="font-mono text-xs">
                  {c}
                </code>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyCodes}>
                {copied ? <Check className="text-green-600" /> : <Copy />}
                {copied ? "Copied" : "Copy codes"}
              </Button>
              <Button
                onClick={() => {
                  setStep("idle");
                  setEnrolment(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
